import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Briefcase, Shield, UserCog, User } from "lucide-react";

function initials(name) {
  return (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function ringClass(role) {
  if (role === "admin") return "border-[color:var(--accent)]";
  if (role === "manager") return "border-[#00e5ff]";
  return "border-[#2a3447]";
}

function roleBadge(role) {
  if (role === "admin") return { icon: Shield, label: "Admin", cls: "bg-[color:var(--accent)]/15 text-[color:var(--accent)] border-[color:var(--accent)]/40" };
  if (role === "manager") return { icon: UserCog, label: "Manager", cls: "bg-[#00e5ff]/15 text-[#00e5ff] border-[#00e5ff]/40" };
  return { icon: User, label: "Employee", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" };
}

function renderTree(roots) {
  const render = (node, depth) => {
    const badge = roleBadge(node.role);
    const BadgeIcon = badge.icon;
    return (
      <div className="flex flex-col items-center relative" key={node.id} data-testid={`org-node-${node.id}`}>
        <div className={`card-surface p-4 min-w-[220px] max-w-[240px] border-2 ${ringClass(node.role)} text-center`}>
          <div className="relative w-14 h-14 mx-auto">
            {node.avatar_url ? (
              <img src={node.avatar_url} alt={node.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#2a3447]" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#242c3d] flex items-center justify-center font-bold text-[color:var(--accent)] border-2 border-[#2a3447]">
                {initials(node.name)}
              </div>
            )}
            <span className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full grid place-items-center border ${badge.cls}`} title={badge.label}>
              <BadgeIcon size={11} />
            </span>
          </div>
          <div className="font-bold mt-3 text-sm truncate" data-testid={`org-name-${node.id}`}>{node.name}</div>
          {(node.job || node.title) && (
            <div className="text-[11px] mt-1 inline-flex items-center gap-1 text-[color:var(--accent)] font-semibold uppercase tracking-widest" data-testid={`org-job-${node.id}`}>
              <Briefcase size={10} /> {node.job || node.title}
            </div>
          )}
          {node.department && (
            <div className="text-[10px] text-slate-500 mt-1 truncate">{node.department}</div>
          )}
          <div className={`inline-block mt-2 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${badge.cls}`}>
            {badge.label}
          </div>
        </div>
        {node.children && node.children.length > 0 && depth < 6 && (
          <>
            <div className="w-px h-6 bg-[#2a3447]" />
            <div className="flex gap-6 relative">
              <div className="absolute top-0 left-0 right-0 h-px bg-[#2a3447]" />
              {node.children.map((c) => (
                <div key={c.id} className="pt-6 relative">
                  <div className="absolute top-0 left-1/2 w-px h-6 bg-[#2a3447]" />
                  {render(c, depth + 1)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };
  return roots.map((r) => render(r, 0));
}

export default function OrgChart() {
  const [tree, setTree] = useState([]);
  useEffect(() => {
    api.get("/org/tree").then(({ data }) => setTree(data));
  }, []);

  return (
    <div className="space-y-6" data-testid="org-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Company Structure</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Org Hierarchy</h1>
        <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[color:var(--accent)]" /> Admin</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#00e5ff]" /> Manager</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Employee</span>
        </div>
      </div>

      <div className="card-surface p-8 overflow-auto">
        <div className="flex gap-12 min-w-max justify-center">
          {renderTree(tree)}
        </div>
      </div>
    </div>
  );
}
